// @flow

import {DOCUMENTS_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {
  patchJson,
  postJson,
  get,
  del,
  createErrorHandler,
  showForbiddenMessageHandler,
} from 'app/utils/request';
import {showNotification} from 'app/toasts/slice';
import {DEFAULT_PAGE_SIZE, SORT_BY_TYPES} from 'app/utils/constants';

import type {
  DashboardLocationParamsType,
  DashboardFetchParamsType,
  DocumentType,
  DashboardFoldersFetchParamsType,
} from './types';

export const REQUEST_DOCUMENTS_DASHBOARD_LIST =
  'REQUEST_DOCUMENTS_DASHBOARD_LIST';
export const RECEIVE_DOCUMENTS_DASHBOARD_LIST =
  'RECEIVE_DOCUMENTS_DASHBOARD_LIST';
export const RECEIVE_DOCUMENT = 'RECEIVE_DOCUMENT';
export const TOGGLE_DOCUMENTS_DASHBOARD_RELATION =
  'TOGGLE_DOCUMENTS_DASHBOARD_RELATION';

import {limitRequests} from 'app/utils/redux';
import type {ResponseType} from 'app/utils/redux_v2';
import type {contentType} from 'app/utils/content-type';

export const requestDocumentsDashboardList = (
  currentFilters: DashboardLocationParamsType
) => ({
  type: 'REQUEST_DOCUMENTS_DASHBOARD_LIST',
  currentFilters: {...currentFilters},
});

type RequestDocumentsDashboardList = {
  type: 'REQUEST_DOCUMENTS_DASHBOARD_LIST',
  currentFilters: DashboardLocationParamsType,
};

type ReceiveDocumentsDashboardList = {
  type: 'RECEIVE_DOCUMENTS_DASHBOARD_LIST',
  query: DashboardFetchParamsType,
  response: ResponseType<DocumentType>,
};

type ToggleDocumentsDashboardRelation = {
  type: 'TOGGLE_DOCUMENTS_DASHBOARD_RELATION',
  relation: contentType,
};

type ReceiveDocument = {
  type: 'RECEIVE_DOCUMENT',
  document: DocumentType,
};

export type DocumentActionType =
  | RequestDocumentsDashboardList
  | ReceiveDocumentsDashboardList
  | ReceiveDocument
  | ToggleDocumentsDashboardRelation;

export const receiveDocumentsDashboardList = (
  query: DashboardFetchParamsType,
  response: ResponseType<DocumentType>
) => ({
  type: 'RECEIVE_DOCUMENTS_DASHBOARD_LIST',
  query,
  response,
});

export const receiveDocument = (document: DocumentType) => ({
  type: 'RECEIVE_DOCUMENT',
  document,
});

export const toggleDocumentsDashboardRelation = (relation: contentType) => ({
  type: 'TOGGLE_DOCUMENTS_DASHBOARD_RELATION',
  relation,
});

export const convertLocationParamsFoldersDocumentsToFetchParams = (
  query: DashboardLocationParamsType
): DashboardFoldersFetchParamsType => {
  return {
    page: query.folders_page != null ? query.folders_page : '1',
    page_size:
      query.folders_page_size != null
        ? query.folders_page_size
        : DEFAULT_PAGE_SIZE,
    parent: query.folder != null ? query.folder : '',
    team_id:
      query.team_id != null && query.team_id !== '-1'
        ? query.team_id
        : undefined,
    permission: query.permission,
    search: query.search ?? '',
  };
};

export const convertLocationParamsToFetchParams = (
  query: DashboardLocationParamsType
): DashboardFetchParamsType => {
  const filters = query.filter != null ? query.filter.split(',') : [];

  return {
    team_id:
      query.team_id != null && query.team_id !== '-1'
        ? query.team_id
        : undefined,
    in_no_teams: query.team_id === '-1' ? '1' : undefined,
    exclude_team_id: query.exclude_team_id,
    page: query.page != null ? query.page : '1',
    page_size: query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE,
    search: query.search,
    ordering: query.ordering,
    private:
      filters.indexOf('private') > -1
        ? 'true'
        : filters.indexOf('public') > -1
        ? 'false'
        : undefined,
    tag: query.tag != null ? query.tag : undefined,
    visible_to_user: query.visible_to_user,
    filter: query.filter,
    folder: query.folder || '', // forces to fetch from top level
  };
};

export const convertLocationParamsToFetchParamsExcludeFolder = (
  query: DashboardLocationParamsType
): DashboardFetchParamsType => {
  const params = convertLocationParamsToFetchParams(query);
  params.folder = '';
  return params;
};

export const fetchDocuments =
  (fetchParams: DashboardFetchParamsType) => (dispatch: SimpleDispatch) =>
    requestDocuments(fetchParams).then(
      (response) =>
        dispatch(receiveDocumentsDashboardList(fetchParams, response)),
      createErrorHandler(gt('Unable to load the documents'), dispatch)
    );

export const fetchDocumentsForDashboard = limitRequests(
  (locationParams: DashboardLocationParamsType) => (dispatch: SimpleDispatch) => {
    dispatch(requestDocumentsDashboardList(locationParams));
    const fetchParams = convertLocationParamsToFetchParams(locationParams);
    return ((dispatch: any): FuncDispatch)(fetchDocuments(fetchParams));
  }
);

export const resetCurrentDocumentsFilters =
  (locationParams: DashboardLocationParamsType) => (dispatch: SimpleDispatch) =>
    dispatch(requestDocumentsDashboardList(locationParams));

/* === fetch single === */
export const fetchDocument = (url: string) => (dispatch: SimpleDispatch) =>
  get(url).then(
    (doc) => dispatch(receiveDocument(doc)),
    createErrorHandler(gt('Unable to load the document'), dispatch)
  );

/* === fetch list === */

const requestDocuments = (params: DashboardFetchParamsType) =>
  get(DOCUMENTS_API_URL, params);

export const searchDocuments = (
  search?: string,
  page_size: number = DEFAULT_PAGE_SIZE,
  search_fields: 'name' = 'name',
  ordering: string = SORT_BY_TYPES.RANK,
  page: string = '1'
) => requestDocuments({search, page, page_size, search_fields, ordering});

/* === create new === */

export const createNewDocument =
  (data: {name?: string} = {}) =>
  (dispatch: SimpleDispatch) =>
    // https://twitter.com/dan_abramov/status/770914221638942720
    postJson(DOCUMENTS_API_URL, data).catch(
      createErrorHandler(gt('Could not create the document.'), dispatch)
    );

export const deleteDocument = (url: string) => (dispatch: FuncDispatch) =>
  del(url).then(
    () => dispatch(showNotification(gt('Document moved to Recycle Bin'))),
    createErrorHandler(
      gt('Unable to delete document'),
      dispatch,
      showForbiddenMessageHandler(true)
    )
  );

/* === update === */

export const setPrivate =
  ({url, isPrivate}: {url: string, isPrivate: boolean}) =>
  (dispatch: FuncDispatch) =>
    patchJson(url, {private: isPrivate})
      .then((doc) => {
        dispatch(fetchDocument(doc.url));

        return dispatch(
          showNotification(
            isPrivate
              ? gt('This document is now private')
              : gt('This document is now public')
          )
        );
      })
      .catch(
        createErrorHandler(
          gt('Unable to update document. No changes were made.'),
          dispatch
        )
      );
